import { XHRError, XHRResponse } from "@/mk2/helpers/api";
import { GetParams, JSONData, serializeGetParams } from "@/mk2/helpers/urls";

export async function makeRequest(
    method: "GET" | "POST",
    url: string,
    data: GetParams | JSONData,
    headers: Record<string, string>,
    fetchFunc: typeof fetch,
): Promise<XHRResponse> {
    headers = {
        // used by Django to detect AJAX request (request.is_ajax() is True)
        ...headers,
        "X-Requested-With": "XMLHttpRequest",
    };
    const init: RequestInit = {
        method,
        headers,
        // tells fetch() to send cookies with the request,
        // even for cross-origin requests
        credentials: "include",
    };

    if (data) {
        if (method === "GET") {
            const qs = serializeGetParams(data as GetParams);
            if (qs) {
                url += (url.includes("?") ? "&" : "?") + qs;
            }
        } else {
            headers["Content-Type"] = "application/json";
            init.body = JSON.stringify(data);
        }
    }

    let fetchResponse: Response;
    try {
        // On server (nodejs) ensure that fetchFunc is undici.fetch() not
        // default fetch(), so that protected headers such as Cookie and
        // Referer can be used
        //
        // In browser we use default fetch() and do not need to use
        // special headers()
        fetchResponse = await fetchFunc(url, init);
    } catch (networkErr) {
        // Network error (CORS, offline, DNS failure, etc.)
        const err = new Error(
            networkErr instanceof Error ? networkErr.message : "Network request failed",
        ) as XHRError<any>;
        err.status = 400;
        err.errorCode = "NETWORK_ERROR";
        err.response = { body: {} };
        throw err;
    }

    const xhrResponse: XHRResponse = {
        // explicitly copy the values, because
        // ...fetchResponse destruct does not work
        status: fetchResponse.status,
        statusText: fetchResponse.statusText,
        headers: fetchResponse.headers,
        body: undefined,
        type: fetchResponse.type,
        url: fetchResponse.url,
    } as XHRResponse;
    try {
        const body: any = await fetchResponse.json();
        xhrResponse.body = body;
    } catch (jsonErr) {
        xhrResponse.body = {};

        if (
            fetchResponse.ok &&
            // 204 No Content
            fetchResponse.status !== 204
        ) {
            const err = new Error(
                jsonErr instanceof Error ? jsonErr.message : "Failed to parse JSON response",
            ) as XHRError<any>;
            err.status = 500;
            err.errorCode = "JSON_PARSE_ERROR";
            err.response = xhrResponse;
            throw err;
        }
    }

    if (!fetchResponse.ok) {
        const err: any = new Error(`${xhrResponse.status} ${xhrResponse.statusText}`);
        err.status = xhrResponse.status;
        err.response = xhrResponse;
        throw err;
    }

    return xhrResponse;
}

const DEFAULT_NUMBER_OF_RETRIES = 1;

/**
 * Try to make request - if it fails with NETWORK_ERROR error, retry it
 *
 * @param apiAuth
 * @param method {string}
 * @param url {string}
 * @param data {object}
 */
export async function makeRequestWithRetries(
    method: "GET",
    url: string,
    data: GetParams | JSONData,
    headers: Record<string, string>,
    fetchFunc: typeof fetch,
): Promise<XHRResponse> {
    const numOfRetries = DEFAULT_NUMBER_OF_RETRIES;

    let i = 0;
    while (true) {
        try {
            const res = await makeRequest(method, url, data, headers, fetchFunc);
            return res;
        } catch (err) {
            if (err.errorCode == "NETWORK_ERROR" && i < numOfRetries) {
                i++;

                // Retry request
                continue;
            }

            throw err; // Re-throw
        }
    }
}
